import { Fragment, type ReactNode } from 'react';
import { diffLists, diffWords } from '@/lib/diff';

export interface DiffField {
  /** Material field name, e.g. "To", "Subject", "Body", "Attachments". */
  name: string;
  before: string | readonly string[] | null | undefined;
  after: string | readonly string[] | null | undefined;
  /** Render list values in monospace (addresses, hashes). */
  mono?: boolean;
}

function isList(v: DiffField['before']): v is readonly string[] {
  return Array.isArray(v);
}

function fieldChanged(f: DiffField): boolean {
  if (isList(f.before) || isList(f.after)) {
    const b = isList(f.before) ? f.before : [];
    const a = isList(f.after) ? f.after : [];
    return b.length !== a.length || b.some((x, i) => x !== a[i]);
  }
  return (f.before ?? '') !== (f.after ?? '');
}

/** Plain statement of what changes, e.g. "Body unchanged; To and Subject change." Never "no changes" if any field differs. */
export function summarizeDiff(fields: readonly DiffField[]): string {
  const changed = fields.filter(fieldChanged).map((f) => f.name);
  const same = fields.filter((f) => !fieldChanged(f)).map((f) => f.name);
  if (changed.length === 0) return `All compared fields identical (${same.join(', ')}).`;
  const join = (xs: string[]) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);
  if (same.length === 0) return `${join(changed)} ${changed.length === 1 ? 'changes' : 'change'}.`;
  return `${join(same)} unchanged; ${join(changed)} ${changed.length === 1 ? 'changes' : 'change'}.`;
}

function Removed({ children }: { children: ReactNode }) {
  return (
    <del className="diff-del">
      <span className="visually-hidden">[removed: </span>
      {children}
      <span className="visually-hidden">]</span>
    </del>
  );
}
function Added({ children }: { children: ReactNode }) {
  return (
    <ins className="diff-ins">
      <span className="visually-hidden">[added: </span>
      {children}
      <span className="visually-hidden">]</span>
    </ins>
  );
}

function ListSide({ values, marks, kind, mono }: { values: readonly string[]; marks: Set<string>; kind: 'before' | 'after'; mono?: boolean }) {
  if (values.length === 0) return <span className="unknown-value">None</span>;
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }} className={mono ? 'mono' : undefined}>
      {values.map((v) => (
        <li key={v}>{marks.has(v) ? kind === 'before' ? <Removed>{v}</Removed> : <Added>{v}</Added> : <span className="diff-unchanged">{v}</span>}</li>
      ))}
    </ul>
  );
}

function TextSides({ before, after }: { before: string; after: string }) {
  const ops = diffWords(before, after);
  return (
    <>
      <div className="diff-side" data-changed={before !== after ? 'before' : undefined}>
        {before === '' ? <span className="unknown-value">Empty</span> : ops.filter((o) => o.kind !== 'insert').map((o, i) =>
          o.kind === 'delete' ? <Removed key={i}>{o.text}</Removed> : <Fragment key={i}>{o.text}</Fragment>,
        )}
      </div>
      <div className="diff-side" data-changed={before !== after ? 'after' : undefined}>
        {after === '' ? <span className="unknown-value">Empty</span> : ops.filter((o) => o.kind !== 'delete').map((o, i) =>
          o.kind === 'insert' ? <Added key={i}>{o.text}</Added> : <Fragment key={i}>{o.text}</Fragment>,
        )}
      </div>
    </>
  );
}

/**
 * Before / after comparison over every material field. Removed and added text
 * carry semantic del/ins plus hidden text labels, so meaning never depends on colour.
 */
export function DiffView({
  fields,
  beforeLabel = 'Before',
  afterLabel = 'After',
  hideUnchanged = false,
}: {
  fields: readonly DiffField[];
  beforeLabel?: ReactNode;
  afterLabel?: ReactNode;
  hideUnchanged?: boolean;
}) {
  const shown = hideUnchanged ? fields.filter(fieldChanged) : fields;
  return (
    <div className="diff" role="group" aria-label="Comparison">
      <p className="diff-summary" role="status">
        {summarizeDiff(fields)}
      </p>
      <div className="diff-field diff-head" aria-hidden>
        <div className="diff-field-name">Field</div>
        <div className="diff-side">{beforeLabel}</div>
        <div className="diff-side">{afterLabel}</div>
      </div>
      {shown.map((f) => {
        const list = isList(f.before) || isList(f.after);
        if (list) {
          const b = isList(f.before) ? f.before : [];
          const a = isList(f.after) ? f.after : [];
          const d = diffLists(b, a);
          return (
            <div className="diff-field" key={f.name}>
              <div className="diff-field-name">{f.name}</div>
              <div className="diff-side" data-changed={d.removed.length ? 'before' : undefined}>
                <ListSide values={b} marks={new Set(d.removed)} kind="before" mono={f.mono} />
              </div>
              <div className="diff-side" data-changed={d.added.length ? 'after' : undefined}>
                <ListSide values={a} marks={new Set(d.added)} kind="after" mono={f.mono} />
              </div>
            </div>
          );
        }
        return (
          <div className="diff-field" key={f.name}>
            <div className="diff-field-name">{f.name}</div>
            <TextSides before={(f.before as string | null | undefined) ?? ''} after={(f.after as string | null | undefined) ?? ''} />
          </div>
        );
      })}
    </div>
  );
}
